import { CalendarCheck, ChevronDown, CircleHelp, ListTree } from "lucide-react"
import { useState, type ReactNode } from "react"

import { useUi } from "@/core/i18n/locale-context"

import type { Guide } from "../../content/guide-types"
import { AnswerBox } from "./answer-box"
import { Breadcrumb } from "./breadcrumb"
import { CtaBand } from "./cta-band"
import { FaqAccordion } from "./faq-accordion"
import { HeroBand, SectionTitle } from "./page-parts"
import { RelatedLinks } from "./related-links"
import { SourceLine } from "./source-line"

/**
 * Answer box. With `more`, phones show the first part and a "More" button; the rest and the source stay in the DOM
 * (hidden below md until opened), and from md everything shows as before.
 */
function GuideAnswer({ guide }: { guide: Guide }) {
  const ui = useUi()
  const [open, setOpen] = useState(false)
  const source = (
    <SourceLine
      sources={guide.sources}
      financialYear={guide.financialYear}
      checked={guide.checked}
    />
  )
  if (!guide.answerMore) return <AnswerBox source={source}>{guide.answer}</AnswerBox>
  const hide = open ? "" : "max-md:hidden"
  const more: ReactNode = (
    <>
      <div className={hide}>{source}</div>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="text-primary focus-visible:ring-ring -my-1 flex min-h-11 w-fit items-center gap-1 rounded-sm text-sm font-medium outline-none focus-visible:ring-2 md:hidden"
      >
        {open ? ui.less : ui.more}
        <ChevronDown className={`size-4 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
    </>
  )
  return (
    <AnswerBox source={more} compact>
      {guide.answer} <span className={hide}>{guide.answerMore}</span>
    </AnswerBox>
  )
}

/** Shared template for /guides/$slug and /cents-per-km-calculator: answer box with source, body, FAQ, CTA, desktop contents. */
export function GuidePage({ guide }: { guide: Guide }) {
  const ui = useUi()
  return (
    <>
      <Breadcrumb
        items={[
          { label: ui.home, href: "/" },
          { label: ui.guides, href: "/guides" },
          { label: guide.crumb },
        ]}
      />
      <HeroBand className={guide.answerMore ? "max-md:gap-3 max-md:py-4" : ""}>
        <p className="text-muted-foreground flex flex-wrap items-center gap-3 text-sm">
          {guide.badge && (
            <span className="bg-accent text-accent-foreground rounded-full px-2.5 py-0.5 text-xs font-semibold">
              {guide.badge}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5">
            <CalendarCheck className="size-4" aria-hidden="true" />
            {ui.lastReviewed} {ui.longDate(guide.reviewed)}
            <span className={guide.answerMore ? "max-md:hidden" : ""}>
              {" "}
              · {ui.ratesLine(guide.financialYear)}
            </span>
          </span>
        </p>
        <h1
          className={`font-bold tracking-tight md:text-5xl ${guide.answerMore ? "text-[1.625rem] leading-tight" : "text-3xl"}`}
        >
          {guide.h1}
        </h1>
        {guide.figure ? (
          // The worked example sits beside the answer from md; on phones the calculator below shows the same numbers.
          <div
            data-hero-beside=""
            className="grid items-center gap-6 md:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]"
          >
            <div className="max-w-[70ch]">
              <GuideAnswer guide={guide} />
            </div>
            <div className="max-md:hidden">{guide.figure}</div>
          </div>
        ) : (
          <div className="max-w-[70ch]">
            <GuideAnswer guide={guide} />
          </div>
        )}
      </HeroBand>

      <div
        className={`px-band grid gap-8 py-6 lg:grid-cols-[minmax(0,1fr)_14rem] ${guide.answerMore ? "max-md:pt-4" : ""}`}
      >
        <article className="grid min-w-0 gap-8">
          {guide.body}

          {guide.faq && (
            <section aria-labelledby="faq" className="grid gap-3">
              <SectionTitle id="faq" icon={CircleHelp} tone="success">
                {ui.commonQuestions}
              </SectionTitle>
              <FaqAccordion items={guide.faq} defaultOpen={0} />
            </section>
          )}

          <p className="text-muted-foreground text-sm">{ui.guideDisclaimer}</p>
        </article>

        <aside className="hidden lg:block">
          <nav
            aria-label={ui.onThisPage}
            className="border-border bg-card sticky top-6 grid gap-2 rounded-xl border p-4 text-sm"
          >
            <b className="flex items-center gap-2">
              <ListTree className="size-4" aria-hidden="true" />
              {ui.onThisPage}
            </b>
            {guide.toc.map(({ id, label }) => (
              <a
                key={id}
                href={`#${id}`}
                className="text-muted-foreground hover:text-foreground focus-visible:ring-ring rounded-sm outline-none focus-visible:ring-2"
              >
                {label}
              </a>
            ))}
          </nav>
        </aside>
      </div>

      <RelatedLinks links={guide.related} />

      <CtaBand {...(guide.cta ?? ui.guideCta)} />
    </>
  )
}
